/**
 * Établissement courant : sa configuration, ses salles, ses consignes, et les
 * passages en cours.
 *
 * Un seul magasin pour les trois, parce qu'ils se rafraîchissent ensemble à
 * chaque événement temps réel et que les écrans les affichent côte à côte. Le
 * choix d'établissement est conservé d'une visite à l'autre : un soignant
 * rattaché à deux hôpitaux ne veut pas le redire à chaque ouverture.
 */

import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { apiFetch } from '../lib/api';
import { onFitgoEvent } from '../lib/socket';

export interface Room {
  id: string;
  name: string;
  type: 'BOX' | 'SALLE_ATTENTE' | 'SALLE_ECHO' | 'SALLE_NAISSANCE' | 'AUTRE';
  capacity: number;
  position: number;
  active: boolean;
}

export interface Hospital {
  id: string;
  slug: string;
  name: string;
  city: string;
  termThresholdWeeks: number;
  gynecologyEnabled: boolean;
  obstetricsEnabled: boolean;
  exportPeriod: 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
  rooms?: Room[];
}

export interface Instruction {
  level: number;
  targetDelayMinutes: number;
  placement: string;
  notifyRoles: string[];
  notifyImmediately: boolean;
  extraActions: string[];
  overridden: boolean;
}

export interface Caregiver {
  id: string;
  user: { id: string; displayName: string; initials: string };
}

export interface Visit {
  id: string;
  number: number;
  year: number;
  lastNamePrefix: string;
  firstNameInitial: string;
  birthDate: string;
  state: 'ARRIVEE' | 'EN_ATTENTE' | 'EN_ATTENTE_EXAMEN' | 'EN_ATTENTE_AVIS' | 'PRISE_EN_CHARGE' | 'SORTIE';
  stream: 'GYNECOLOGIQUE' | 'OBSTETRICAL';
  motifLabel: string;
  arrivedAt: string;
  triagedAt: string | null;
  careStartedAt: string | null;
  dischargedAt: string | null;
  disposition: string | null;
  roomId: string | null;
  room: { id: string; name: string; type: string } | null;
  triage: {
    computedLevel: number;
    finalLevel: number;
    overrideReason: string | null;
  } | null;
  caregivers: Caregiver[];
}

const STORAGE_KEY = 'fitgo.hospital';

export const useHospitalStore = defineStore('hospital', () => {
  const hospitals = ref<Hospital[]>([]);
  const currentId = ref<string | null>(null);
  const current = ref<Hospital | null>(null);
  const instructions = ref<Instruction[]>([]);
  const visits = ref<Visit[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const rooms = computed(() => current.value?.rooms ?? []);

  /** Patientes en attente sans salle, triées par niveau puis par ancienneté (section 8.2). */
  const waitingWithoutRoom = computed(() =>
    visits.value
      .filter((visit) => visit.roomId === null && visit.dischargedAt === null)
      .sort((a, b) => {
        const levelA = a.triage?.finalLevel ?? 5;
        const levelB = b.triage?.finalLevel ?? 5;
        if (levelA !== levelB) return levelA - levelB;
        return a.arrivedAt.localeCompare(b.arrivedAt);
      }),
  );

  function instructionFor(level: number): Instruction | null {
    return instructions.value.find((i) => i.level === level) ?? null;
  }

  async function loadHospitals(): Promise<void> {
    const payload = await apiFetch<{ hospitals: Hospital[] }>('/api/hospitals');
    hospitals.value = payload.hospitals;

    const remembered = localStorage.getItem(STORAGE_KEY);
    const stillValid =
      remembered !== null && payload.hospitals.some((h) => h.id === remembered);

    const next = stillValid ? remembered : (payload.hospitals[0]?.id ?? null);
    if (next !== null) await select(next);
  }

  async function select(hospitalId: string): Promise<void> {
    currentId.value = hospitalId;
    try {
      localStorage.setItem(STORAGE_KEY, hospitalId);
    } catch {
      // Navigation privée : le choix ne sera pas retenu, ce n'est pas bloquant.
    }
    await Promise.all([loadConfig(), loadVisits()]);
  }

  async function loadConfig(): Promise<void> {
    if (currentId.value === null) return;
    const payload = await apiFetch<{
      hospital: Hospital;
      effectiveInstructions: Instruction[];
    }>(`/api/hospitals/${currentId.value}`);
    current.value = payload.hospital;
    instructions.value = payload.effectiveInstructions;
  }

  async function loadVisits(): Promise<void> {
    if (currentId.value === null) return;
    loading.value = true;
    error.value = null;
    try {
      const payload = await apiFetch<{ visits: Visit[] }>(
        `/api/hospitals/${currentId.value}/visits`,
      );
      visits.value = payload.visits;
    } catch (cause) {
      error.value = cause instanceof Error ? cause.message : 'Chargement impossible.';
    } finally {
      loading.value = false;
    }
  }

  // Tout événement de l'établissement courant recharge les passages. Recharger
  // la liste entière plutôt que patcher un élément : à cette taille, c'est
  // imperceptible, et cela évite qu'un écran affiche un état inventé.
  onFitgoEvent((event) => {
    if (currentId.value === null) return;
    if (event.type === 'rooms:changed' || event.type === 'config:changed') {
      void loadConfig();
      return;
    }
    void loadVisits();
  });

  return {
    hospitals,
    currentId,
    current,
    instructions,
    visits,
    rooms,
    loading,
    error,
    waitingWithoutRoom,
    instructionFor,
    loadHospitals,
    loadConfig,
    loadVisits,
    select,
  };
});
